"use client";

import type { ReactNode, SVGProps } from "react";

/**
 * Основа иконочного пака YUC Portal.
 *
 * Спецификация (см. docs/reference/icon-pack.md):
 * - грид 24×24, живая область 20×20 (инсет 2)
 * - штрих 1.5 по умолчанию, скруглённые концы и стыки
 * - ТОЛЬКО currentColor — иконка наследует цвет живой темы из backend JSON
 * - размер в ПИКСЕЛЯХ (rem в проекте = 12px, Tailwind w-4 = 12px → размеры иконок не через w-/h-)
 * - внутри <svg> НЕТ transform/filter (ломает рендер в prod-сборке,
 *   см. память feedback_svg_transform_prod_render)
 */
export type IconProps = {
  /** Размер в px (не rem!). Шкала пака: 12 / 14 / 16 / 20 / 24. */
  size?: number;
  /** Толщина штриха на гриде 24. По умолчанию 1.5. */
  strokeWidth?: number;
  /** Duotone: заливка вторичного объёма currentColor с opacity. */
  duotone?: boolean;
  /** Доступное имя. Без него иконка скрыта от скринридера (aria-hidden). */
  title?: string;
  className?: string;
} & Omit<SVGProps<SVGSVGElement>, "width" | "height" | "ref">;

/** Вторичный слой duotone — заливка currentColor, БЕЗ штриха. */
export function Duo({ on, children }: { on: boolean; children: ReactNode }) {
  if (!on) return null;
  return (
    <g fill="currentColor" stroke="none" opacity={0.16}>
      {children}
    </g>
  );
}

/** Точка/кружок-заливка (кнопки калькулятора, узлы схем и т.п.). */
export function Dot({ cx, cy, r = 1 }: { cx: number; cy: number; r?: number }) {
  return <circle cx={cx} cy={cy} r={r} fill="currentColor" stroke="none" />;
}

export function makeIcon(displayName: string, render: (duotone: boolean) => ReactNode) {
  function Icon({
    size = 16,
    strokeWidth = 1.5,
    duotone = false,
    title,
    className,
    ...rest
  }: IconProps) {
    return (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        role={title ? "img" : undefined}
        aria-hidden={title ? undefined : true}
        focusable="false"
        {...rest}
      >
        {title ? <title>{title}</title> : null}
        {render(duotone)}
      </svg>
    );
  }
  Icon.displayName = displayName;
  return Icon;
}
